import { useEffect, useState } from "react";
import axios from "axios";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, ImageIcon, RefreshCw, Lock, Globe, RotateCcw } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Card, Modal } from "@/components/cc";

const PUBLIC_CLASSES = ["article_image", "crime_image", "video_thumbnail", "quote_graphic", "author_image", "book_cover"];
const PRIVATE_CLASSES = ["draft_image", "research_document", "unpublished_quote", "unpublished_thumbnail", "private_evidence", "editorial_asset"];
const ACCEPT = ["image/jpeg", "image/png", "image/webp", "image/gif"];

function Thumb({ asset, adminOnly }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    // Deleted assets can't be served via the public endpoint, so the Trash view always
    // uses the authenticated admin file endpoint.
    const path = (!adminOnly && asset.visibility === "public")
      ? `/public/media/${asset.id}?variant=thumb`
      : `/media/file/${asset.id}?variant=thumb`;
    api.get(path, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [asset.id, asset.visibility, adminOnly]);
  return src
    ? <img src={src} alt="" className="w-full h-32 object-cover bg-zinc-900" />
    : <div className="w-full h-32 bg-zinc-900 flex items-center justify-center"><ImageIcon className="h-6 w-6 text-zinc-700" /></div>;
}

export default function MediaLibrary() {
  const [assets, setAssets] = useState([]);
  const [tab, setTab] = useState("active"); // active | trash
  const [visibility, setVisibility] = useState("public");
  const [assetClass, setAssetClass] = useState(PUBLIC_CLASSES[0]);
  const [busy, setBusy] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null); // asset pending restore confirmation
  const [restoring, setRestoring] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  const load = (which = tab) => {
    const params = which === "trash" ? { deleted: true } : {};
    api.get("/media/assets", { params }).then((r) => setAssets(r.data)).catch(() => {});
  };
  useEffect(() => {
    load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const classes = visibility === "public" ? PUBLIC_CLASSES : PRIVATE_CLASSES;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPT.includes(file.type)) { toast.error("Only JPEG, PNG, WebP or GIF images are allowed."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/media/uploads", {
        filename: file.name, content_type: file.type, asset_class: assetClass, visibility,
      });
      if (data.mode === "proxy") {
        await api.put(data.upload_url.replace(/^\/api/, ""), file, { headers: { "Content-Type": file.type } });
      } else {
        await axios.put(data.upload_url, file, { headers: data.headers });
        await api.post(`/media/uploads/${data.media_id}/complete`);
      }
      toast.success("Uploaded");
      load("active");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/media/assets/${id}`); toast.success("Moved to Trash"); load("active"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doRestore = async () => {
    if (restoring || !restoreTarget) return;
    setRestoring(true);
    try {
      await api.post(`/media/assets/${restoreTarget.id}/restore`);
      toast.success("Asset restored");
      setRestoreTarget(null);
      load("trash");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setRestoring(false); }
  };
  const reconcile = async () => {
    try { const { data } = await api.get("/media/reconcile"); setReconcileResult(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const TabBtn = ({ id, label }) => (
    <button data-testid={`media-tab-${id}`} onClick={() => setTab(id)}
      className={`px-3 py-1.5 text-sm font-semibold border-b-2 transition-colors ${tab === id ? "border-emerald-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
      {label}
    </button>
  );

  return (
    <div className="p-6">
      <PageHeader title="Media Library" subtitle="Upload and manage public and private media assets (Cloudflare R2).">
        <Btn variant="ghost" data-testid="reconcile-btn" onClick={reconcile}><RefreshCw className="h-4 w-4" /> Reconcile</Btn>
      </PageHeader>

      <div className="flex items-center gap-4 border-b border-zinc-800 mb-6">
        <TabBtn id="active" label="Active" />
        <TabBtn id="trash" label="Trash" />
      </div>

      {tab === "active" && (
        <Card className="p-4 mb-6">
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <Field label="Visibility">
              <select data-testid="media-visibility" className={inputCls} value={visibility}
                onChange={(e) => { setVisibility(e.target.value); setAssetClass(e.target.value === "public" ? PUBLIC_CLASSES[0] : PRIVATE_CLASSES[0]); }}>
                <option value="public">Public</option><option value="private">Private</option>
              </select>
            </Field>
            <Field label="Asset class">
              <select data-testid="media-class" className={inputCls} value={assetClass} onChange={(e) => setAssetClass(e.target.value)}>
                {classes.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </select>
            </Field>
            <label className="inline-flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2 text-sm font-semibold cursor-pointer justify-center hover:bg-black transition-colors">
              <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload image"}
              <input data-testid="media-file-input" type="file" accept={ACCEPT.join(",")} className="hidden" disabled={busy} onChange={onFile} />
            </label>
          </div>
          <p className="mt-2 text-xs text-zinc-500">Allowed: JPEG, PNG, WebP, GIF. SVG is not permitted. Max {15}MB.</p>
        </Card>
      )}

      {tab === "trash" && (
        <p className="text-xs text-zinc-500 mb-4" data-testid="trash-note">
          Deleted assets are kept here and can be restored. They are permanently purged only after the retention period.
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="media-grid">
        {assets.length === 0 && <p className="text-sm text-zinc-600" data-testid="media-empty">{tab === "trash" ? "Trash is empty." : "No media yet."}</p>}
        {assets.map((a) => (
          <Card key={a.id} className="overflow-hidden" data-testid={`media-item-${a.id}`}>
            <Thumb asset={a} adminOnly={tab === "trash"} />
            <div className="p-3">
              <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                {a.visibility === "public" ? <Globe className="h-3.5 w-3.5 text-emerald-400" /> : <Lock className="h-3.5 w-3.5 text-amber-400" />}
                {a.asset_class?.replace(/_/g, " ")}
              </div>
              <div className="text-xs text-zinc-600 mt-1 truncate">{a.original_filename} · {a.width}×{a.height}</div>
              {tab === "active" ? (
                <button data-testid={`media-delete-${a.id}`} onClick={() => del(a.id)} className="mt-2 inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              ) : (
                <div className="mt-2">
                  {a.deleted_at && <div className="text-[0.65rem] text-zinc-600 mb-1">Deleted {new Date(a.deleted_at).toLocaleString()}</div>}
                  <button data-testid={`media-restore-${a.id}`} onClick={() => setRestoreTarget(a)} className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300">
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Modal open={!!reconcileResult} onClose={() => setReconcileResult(null)} title="Storage Reconciliation">
        {reconcileResult && (
          <div className="space-y-3 text-sm" data-testid="reconcile-result">
            <p className="text-xs text-zinc-500">
              Current provider: <span className="font-mono-data text-zinc-300">{reconcileResult.current_storage_provider}</span>.
              Each record is checked only against its own storage provider.
            </p>
            {[
              ["current_provider_active_missing", "Active assets missing their object", "text-red-400", "Needs attention — active asset with no object in the current provider."],
              ["current_provider_deleted_missing", "Deleted assets missing their object", "text-amber-400", "Soft-deleted; object gone so it could not be restored."],
              ["cross_provider_legacy", "Cross-provider legacy records", "text-zinc-300", "Belong to a previous provider; not data loss."],
              ["unknown_provider_legacy", "Unknown-provider legacy records", "text-zinc-300", "No storage_provider recorded; left untouched."],
              ["orphans", "Orphan objects (no DB record)", "text-zinc-300", "Objects in the current provider with no matching record."],
            ].map(([key, label, color, desc]) => (
              <div key={key} className="flex items-start justify-between gap-3 border border-zinc-800 rounded-sm px-3 py-2" data-testid={`reconcile-${key}`}>
                <div>
                  <div className={`font-semibold ${color}`}>{label}</div>
                  <div className="text-xs text-zinc-500">{desc}</div>
                </div>
                <div className={`text-xl font-tactical font-black ${color}`} data-testid={`reconcile-${key}-count`}>{reconcileResult[key]?.count ?? 0}</div>
              </div>
            ))}
            <div className="text-xs text-zinc-500 pt-1">
              Pending uploads: {reconcileResult.pending_uploads?.count ?? 0} · Abandoned: {reconcileResult.abandoned_uploads?.count ?? 0} · Purgeable (soft-deleted &gt; {reconcileResult.retention_days}d): {reconcileResult.purgeable_soft_deleted ?? 0}
            </div>
            <Btn data-testid="reconcile-close-btn" onClick={() => setReconcileResult(null)} className="w-full justify-center">Close</Btn>
          </div>
        )}
      </Modal>

      <Modal open={!!restoreTarget} onClose={() => { if (!restoring) setRestoreTarget(null); }} title="Restore asset">
        {restoreTarget && (
          <div className="space-y-4" data-testid="restore-confirm">
            <p className="text-sm text-zinc-400">Restore this asset back to the active Media Library?</p>
            <div className="text-sm font-mono-data break-all" data-testid="restore-confirm-name">{restoreTarget.original_filename}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="restore-cancel-btn" disabled={restoring} onClick={() => setRestoreTarget(null)}>Cancel</Btn>
              <Btn data-testid="restore-confirm-btn" disabled={restoring} onClick={doRestore} className="flex-1 justify-center">
                {restoring ? "Restoring…" : "Restore"}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
